import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Code, Settings, Crown } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const CSLEmbedDocs = () => {
  const { toast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);

  const baseUrl = window.location.origin;
  const embedUrl = `${baseUrl}/#/csl-embed`;

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast({
      title: "Copied!",
      description: `${type} code copied to clipboard`,
    });
    setTimeout(() => setCopied(null), 2000);
  };

  const embedCodes = [
    {
      title: "Full CSL Page",
      description: "Complete CSL certification page with all sections",
      code: `<iframe 
  src="${embedUrl}" 
  width="100%" 
  height="1200" 
  frameborder="0" 
  scrolling="auto"
  title="CSL Certification - Full Page">
</iframe>`,
      preview: `${embedUrl}`
    },
    {
      title: "Compact Version",
      description: "Condensed CSL page for smaller spaces",
      code: `<iframe 
  src="${embedUrl}?compact=true" 
  width="100%" 
  height="900" 
  frameborder="0" 
  scrolling="auto"
  title="CSL Certification - Compact">
</iframe>`,
      preview: `${embedUrl}?compact=true`
    },
    {
      title: "Quick Facts Only",
      description: "Just the key CSL certification facts",
      code: `<iframe 
  src="${embedUrl}?hero=false&learning=false&requirements=false&benefits=false&ideal=false&steps=false" 
  width="100%" 
  height="300" 
  frameborder="0" 
  scrolling="auto"
  title="CSL Quick Facts">
</iframe>`,
      preview: `${embedUrl}?hero=false&learning=false&requirements=false&benefits=false&ideal=false&steps=false`
    },
    {
      title: "Leadership Excellence Focus",
      description: "Highlights leadership competencies and expertise areas",
      code: `<iframe 
  src="${embedUrl}?hero=false&facts=false&requirements=false&benefits=false&ideal=false&steps=false" 
  width="100%" 
  height="600" 
  frameborder="0" 
  scrolling="auto"
  title="CSL Leadership Excellence">
</iframe>`,
      preview: `${embedUrl}?hero=false&facts=false&requirements=false&benefits=false&ideal=false&steps=false`
    },
    {
      title: "Requirements & Benefits",
      description: "Shows certification requirements and program benefits",
      code: `<iframe 
  src="${embedUrl}?hero=false&facts=false&learning=false&ideal=false&steps=false" 
  width="100%" 
  height="700" 
  frameborder="0" 
  scrolling="auto"
  title="CSL Requirements & Benefits">
</iframe>`,
      preview: `${embedUrl}?hero=false&facts=false&learning=false&ideal=false&steps=false`
    },
    {
      title: "Minimal Overview",
      description: "Hero section and quick facts only",
      code: `<iframe 
  src="${embedUrl}?learning=false&requirements=false&benefits=false&ideal=false&steps=false" 
  width="100%" 
  height="500" 
  frameborder="0" 
  scrolling="auto"
  title="CSL Overview">
</iframe>`,
      preview: `${embedUrl}?learning=false&requirements=false&benefits=false&ideal=false&steps=false`
    }
  ];

  const parameters = [
    {
      param: "compact",
      type: "boolean",
      default: "false",
      description: "Reduces spacing, font sizes, and content for smaller displays"
    },
    {
      param: "hero",
      type: "boolean", 
      default: "true",
      description: "Shows/hides the main hero section with CSL badge and title"
    },
    {
      param: "facts",
      type: "boolean",
      default: "true", 
      description: "Shows/hides the quick facts cards (cost, duration, etc.)"
    },
    {
      param: "learning",
      type: "boolean",
      default: "true",
      description: "Shows/hides the leadership excellence competencies"
    },
    {
      param: "requirements",
      type: "boolean",
      default: "true",
      description: "Shows/hides the certification requirements section"
    },
    {
      param: "benefits",
      type: "boolean",
      default: "true",
      description: "Shows/hides the program benefits and award process"
    },
    {
      param: "ideal",
      type: "boolean",
      default: "true",
      description: "Shows/hides the ideal candidates section"
    },
    {
      param: "steps",
      type: "boolean",
      default: "true",
      description: "Shows/hides the next steps and eligibility check process"
    },
    {
      param: "width",
      type: "string",
      default: "6xl",
      description: "Container max-width (sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl, 6xl, 7xl)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Crown className="w-8 h-8" />
            <h1 className="text-4xl md:text-5xl font-bold">
              CSL Embed Code Generator
            </h1>
          </div>
          <p className="text-xl text-yellow-100 max-w-3xl">
            Integrate the CSL certification page into your website with customizable sections and layouts
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Quick Start */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              CSL Embed Options
            </CardTitle>
            <CardDescription>
              Choose from these pre-configured embed codes for different use cases
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6">
              {embedCodes.map((embed, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{embed.title}</h3>
                      <p className="text-sm text-slate-600">{embed.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(embed.preview, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(embed.code, embed.title)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        {copied === embed.title ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <pre className="bg-slate-100 p-3 rounded text-sm overflow-x-auto">
                    <code>{embed.code}</code>
                  </pre>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Parameters Reference */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>CSL Embed Parameters</CardTitle>
            <CardDescription>
              Customize the CSL embed using these URL parameters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Parameter</th>
                    <th className="text-left p-3 font-semibold">Type</th>
                    <th className="text-left p-3 font-semibold">Default</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((param, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3">
                        <Badge variant="outline">{param.param}</Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">{param.type}</td>
                      <td className="p-3 text-sm font-mono">{param.default}</td>
                      <td className="p-3 text-sm">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Usage Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Common Use Cases</CardTitle>
            <CardDescription>
              Popular CSL embed configurations for different scenarios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Leadership Showcase</h3>
                <p className="text-sm text-slate-600">
                  Highlight leadership competencies and automatic award process
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?facts=false&ideal=false&steps=false&compact=true`}</code>
                </pre>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold">Executive Summary</h3>
                <p className="text-sm text-slate-600">
                  Full CSL page for executive-level audience
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?width=7xl`}</code>
                </pre>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Requirements Widget</h3>
                <p className="text-sm text-slate-600">
                  Show prerequisites and automatic award process
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?compact=true&hero=false&learning=false&benefits=false&ideal=false&width=sm`}</code>
                </pre>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Achievement Banner</h3>
                <p className="text-sm text-slate-600">
                  Hero section with eligibility check for qualified professionals
                </p>
                <pre className="bg-slate-100 p-3 rounded text-xs overflow-x-auto">
                  <code>{`?facts=false&learning=false&requirements=false&benefits=false&ideal=false&width=3xl`}</code>
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Notes */}
        <Card>
          <CardHeader>
            <CardTitle>CSL Embed Technical Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Recommended Heights</h4>
                <ul className="text-slate-600 space-y-1 ml-4">
                  <li>• Full Page: 1200px</li>
                  <li>• Compact: 900px</li>
                  <li>• Quick Facts Only: 300px</li>
                  <li>• Leadership Excellence: 600px</li>
                  <li>• Requirements & Benefits: 700px</li>
                  <li>• Minimal Overview: 500px</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Automatic Award Process</h4>
                <p className="text-slate-600">
                  CSL is unique as it's automatically awarded upon completion of CSCE and CSP. The embed emphasizes this automatic recognition process and eligibility verification.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Responsive Design</h4>
                <p className="text-slate-600">
                  All CSL embeds are fully responsive and adapt to container width. The compact mode is especially optimized for executive dashboards and leadership portals.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Brand Consistency</h4>
                <p className="text-slate-600">
                  The embed maintains CSL's yellow/gold leadership colors and includes the official leadership badge for visual consistency and authority.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CSLEmbedDocs;